import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  MODULE_PRICING,
  MODULE_DETAILS,
  isModuleAvailable,
  moduleImage,
} from "../constants/moduleSubscriptionCatalog";
import {
  EntityModalTransition,
  entityModalCancelButtonProps,
  entityModalDialogActionsSx,
  entityModalSubmitButtonProps,
  getUnifiedModalPaperProps,
  mergeUnifiedModalTitleSx,
} from "../components/entityModalShell";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  publicCardHoverSx,
  publicCardSx,
  publicHeroChipSx,
  publicSubtitleSx,
  publicTitleSx,
} from "../constants/publicMarketingTheme";

const MAX_SECTIONS = 6;
const MAX_FUNCTIONS = 4;

const PricingPage = () => {
  const navigate = useNavigate();
  const [detailsKey, setDetailsKey] = useState("");

  const activeDetails = detailsKey ? MODULE_DETAILS[detailsKey] : null;
  const activeConfig = MODULE_PRICING.find((m) => m.key === detailsKey);

  return (
    <PublicMarketingShell>
      <Container maxWidth="lg">
        <Chip label="Module pricing" sx={publicHeroChipSx} />
        <Typography variant="h3" sx={{ ...publicTitleSx, mb: 1 }}>
          Pricing
        </Typography>
        <Typography sx={{ ...publicSubtitleSx, mb: 1, maxWidth: 720 }}>
          Each module is priced monthly in PHP. Start with a 7-day trial (all integrated
          modules) or use a subscription code from your account manager. Bundle discounts
          apply when you subscribe to multiple modules at signup.
        </Typography>
        <Typography
          variant="h5"
          sx={{ color: "#f1f5f9", fontWeight: 700, mt: 4, mb: 2 }}
        >
          Modules
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.85)", mb: 2 }}>
          Same catalog as signup: tile, monthly price, and a short description. Use
          &quot;See details&quot; for the full section list and capabilities.
        </Typography>

        <Box
          sx={{
            animation: "fadeUp 650ms ease-out",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
          }}
        >
          {MODULE_PRICING.map((moduleItem) => {
            const details = MODULE_DETAILS[moduleItem.key];
            const selectable = isModuleAvailable(moduleItem);
            const sections = (details?.sections || []).slice(0, MAX_SECTIONS);
            const functions = (details?.functionalities || []).slice(0, MAX_FUNCTIONS);
            return (
              <Card
                key={moduleItem.key}
                sx={{
                  ...publicCardSx,
                  ...publicCardHoverSx,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardMedia
                  component="img"
                  height="128"
                  image={moduleImage(
                    moduleItem.tileImageLabel ?? moduleItem.label,
                    moduleItem.color,
                  )}
                  alt={`${moduleItem.label} module`}
                />
                <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      {moduleItem.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={selectable ? "Available" : "Coming soon"}
                      color={selectable ? "secondary" : "default"}
                      sx={{
                        fontWeight: 600,
                        ...(!selectable && {
                          bgcolor: "rgba(248,113,113,0.18)",
                          color: "#fecaca",
                          borderColor: "rgba(252,165,165,0.45)",
                        }),
                      }}
                      variant={!selectable ? "outlined" : "filled"}
                    />
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ color: "#86efac", fontWeight: 700, mb: 1 }}
                  >
                    PHP {moduleItem.price.toLocaleString()} / month
                  </Typography>
                  {sections.length > 0 ? (
                    <Typography variant="caption" sx={{ color: "rgba(203,213,225,0.95)", mb: 0.5 }}>
                      Includes: {sections.join(" · ")}
                      {(details?.sections?.length || 0) > MAX_SECTIONS ? " · …" : ""}
                    </Typography>
                  ) : null}
                  {functions.length > 0 ? (
                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 1, mb: 0 }}>
                      {functions.map((line, idx) => (
                        <Typography
                          key={`${moduleItem.key}-fn-${idx}`}
                          component="li"
                          variant="body2"
                          sx={{ color: "rgba(226,232,240,0.88)", mb: 0.35 }}
                        >
                          {line}
                        </Typography>
                      ))}
                      {(details?.functionalities?.length || 0) > MAX_FUNCTIONS ? (
                        <Typography variant="caption" sx={{ color: "rgba(148,163,184,0.95)" }}>
                          …and more in full details
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                  <Box sx={{ mt: "auto", pt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setDetailsKey(moduleItem.key)}
                    >
                      See details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Stack direction="row" sx={{ mt: 4, animation: "fadeUp 850ms ease-out" }} spacing={2}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ fontWeight: 700, borderRadius: 2 }}
            onClick={() => navigate("/signup")}
          >
            Start free trial or use a code
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ fontWeight: 600, borderRadius: 2, borderColor: "rgba(255,255,255,0.35)", color: "#e2e8f0" }}
            onClick={() => navigate("/contact")}
          >
            Contact sales
          </Button>
        </Stack>
      </Container>

      <Dialog
        open={Boolean(detailsKey)}
        onClose={() => setDetailsKey("")}
        fullWidth
        maxWidth="sm"
        TransitionComponent={EntityModalTransition}
        keepMounted
        PaperProps={getUnifiedModalPaperProps(detailsKey || "Core")}
      >
        {activeConfig ? (
          <DialogTitle
            sx={mergeUnifiedModalTitleSx(detailsKey || "Core", { pb: 2 })}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ textTransform: "capitalize" }}
            >
              {activeConfig.label}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
              PHP {activeConfig.price.toLocaleString()} / month ·{" "}
              {isModuleAvailable(activeConfig) ? "Available for signup" : "Coming soon"}
            </Typography>
          </DialogTitle>
        ) : null}
        <DialogContent dividers>
          {activeDetails?.sections?.length ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                Included sections
              </Typography>
              <List dense sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 2 }}>
                {activeDetails.sections.map((item) => (
                  <ListItem key={item} sx={{ py: 0.25 }}>
                    <ListItemText primary={item} primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : null}
          {activeDetails?.functionalities?.length ? (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 700 }}>
                Capabilities
              </Typography>
              <List dense sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                {activeDetails.functionalities.map((item) => (
                  <ListItem key={item} sx={{ py: 0.25 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={entityModalDialogActionsSx}>
          <Button
            {...entityModalCancelButtonProps}
            onClick={() => setDetailsKey("")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
          <Button
            {...entityModalSubmitButtonProps}
            onClick={() => navigate("/signup")}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Get started
          </Button>
        </DialogActions>
      </Dialog>
    </PublicMarketingShell>
  );
};

export default PricingPage;
